import React, { useState, useEffect } from 'react';
import { Toolbar, ToolbarSearch, ToolbarSelect, ToolbarSpacer, StatusBadge, Banner, Pagination } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, LCIcon, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import { ARMAssignment } from '@/types/admin';
import { useAuth } from '@/contexts/AuthContext';
import CreateARMAssignmentDialog from '@/components/AdminPanel/ARMAssignments/CreateARMAssignmentDialog';
import ConfirmationDialog from '@/components/AdminPanel/UserManagement/ConfirmationDialog';

type ConfirmState = {
  open: boolean; title: string; message: string;
  onConfirm: () => void; severity?: 'warning' | 'error' | 'info';
};

const ARMAssignments: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [assignments, setAssignments] = useState<ARMAssignment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [createOpen, setCreateOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false, title: '', message: '', onConfirm: () => {}, severity: 'warning',
  });

  const isSuperUser = currentUser?.role === 'super';

  const fetchARMAssignments = async () => {
    setLoading(true); setError(null);
    try {
      const params: any = { page: page + 1, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (activeFilter) params.is_active = activeFilter === 'true';
      const data = await apiService.getARMAssignments(params);
      setAssignments(data.assignments); setTotal(data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch ARM assignments');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchARMAssignments(); }, [page, pageSize, searchTerm, activeFilter]);

  const handleDeactivate = (a: ARMAssignment) => setConfirm({
    open: true, title: 'Deactivate ARM Assignment', severity: 'warning',
    message: `Deactivate the assignment between ${a.arm_user.full_name} (ARM) and ${a.rm_user.full_name} (RM)? This is preserved for audit purposes.`,
    onConfirm: async () => {
      try {
        await apiService.deactivateARMAssignment(a.id);
        setSuccess(`Assignment between ${a.arm_user.full_name} and ${a.rm_user.full_name} deactivated`);
        fetchARMAssignments();
      } catch (err: any) { setError(err.message || 'Failed to deactivate'); }
      setConfirm(p => ({ ...p, open: false }));
    },
  });

  const handleActivate = (a: ARMAssignment) => setConfirm({
    open: true, title: 'Activate ARM Assignment', severity: 'info',
    message: `Activate the assignment between ${a.arm_user.full_name} (ARM) and ${a.rm_user.full_name} (RM)?`,
    onConfirm: async () => {
      try {
        await apiService.activateARMAssignment(a.id);
        setSuccess(`Assignment between ${a.arm_user.full_name} and ${a.rm_user.full_name} activated`);
        fetchARMAssignments();
      } catch (err: any) { setError(err.message || 'Failed to activate'); }
      setConfirm(p => ({ ...p, open: false }));
    },
  });

  return (
    <div className="flex flex-col h-full min-h-0">
      {error && <Banner variant="error" onClose={() => setError(null)} className="mb-3">{error}</Banner>}
      {success && <Banner variant="success" onClose={() => setSuccess(null)} className="mb-3">{success}</Banner>}

      <Toolbar>
        <ToolbarSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search by name or email…" />
        <ToolbarSelect value={activeFilter} onChange={setActiveFilter} placeholder="All statuses"
          options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]} />
        <ToolbarSpacer />
        {!isSuperUser && (
          <LCBtn variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <LCIcon name="plus" size={12} /> Create Assignment
          </LCBtn>
        )}
        <button className={CLS.iconBtn} onClick={fetchARMAssignments} title="Refresh">
          <LCIcon name="refresh" size={14} />
        </button>
      </Toolbar>

      <div className="flex-1 min-h-0 flex flex-col border border-black/[0.08] bg-white">
        <div className="flex-1 min-h-0 overflow-auto">
          <table className={CLS.table}>
            <thead>
              <tr>
                <th className={CLS.th} style={{ width: 60 }}>ID</th>
                <th className={CLS.th}>ARM User</th>
                <th className={CLS.th}>RM User</th>
                <th className={CLS.th}>Assigned Date</th>
                <th className={CLS.th}>Assigned By</th>
                <th className={CLS.th}>Status</th>
                <th className={CLS.th}>Notes</th>
                <th className={`${CLS.th} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className={`${CLS.td} ${CLS.muted} text-center py-8`}>Loading…</td></tr>
              )}
              {!loading && assignments.length === 0 && (
                <tr><td colSpan={8} className={`${CLS.td} ${CLS.muted} text-center py-8`}>No ARM assignments found.</td></tr>
              )}
              {!loading && assignments.map(a => (
                <tr key={a.id} className="hover:bg-[#FAF9F8] transition-colors">
                  <td className={`${CLS.td} ${CLS.tdMono} text-[#7A7A76]`}>{a.id}</td>
                  <td className={CLS.td}>{a.arm_user.full_name}</td>
                  <td className={CLS.td}>{a.rm_user.full_name}</td>
                  <td className={CLS.td}>{new Date(a.assigned_date).toLocaleDateString()}</td>
                  <td className={CLS.td}>{a.assigned_by?.full_name || 'System'}</td>
                  <td className={CLS.td}><StatusBadge active={a.is_active} /></td>
                  <td className={`${CLS.td} max-w-[200px]`}>
                    <span className="block truncate text-[12px] text-[#555]">{a.notes || '—'}</span>
                  </td>
                  <td className={`${CLS.td} text-right`}>
                    {!isSuperUser && (
                      a.is_active ? (
                        <button className={`${CLS.iconBtn} hover:text-[#E50025]`} onClick={() => handleDeactivate(a)} title="Deactivate">
                          <LCIcon name="close" size={13} />
                        </button>
                      ) : (
                        <button className={`${CLS.iconBtn} hover:text-[#1f8a4a]`} onClick={() => handleActivate(a)} title="Activate">
                          <LCIcon name="check" size={13} />
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={total}
          onChange={(p, ps) => { setPage(p); setPageSize(ps); }} />
      </div>

      <CreateARMAssignmentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => { setCreateOpen(false); setSuccess('ARM assignment created successfully'); fetchARMAssignments(); }} />

      <ConfirmationDialog
        open={confirm.open} title={confirm.title} message={confirm.message}
        severity={confirm.severity} onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(p => ({ ...p, open: false }))} />
    </div>
  );
};

export default ARMAssignments;
